import React, { useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Eye, Lock } from 'lucide-react';
import AinaWorkbench from '@/components/AinaWorkbench';
import EarlyBirdModal from '@/components/EarlyBirdModal';
import FeatureHistoryMenu from '@/components/FeatureHistoryMenu';
import { useAuth } from '@/context/AuthContext';
import { recordJourneyVisit } from '@/lib/chatJourney';

const PREMIUM_PLANS = new Set(['PRO', 'TEAM', 'FIRM', 'EARLY_BIRD']);

function hasAinaAccess(user) {
  if (!user) return false;
  const role = String(user.role || '').toUpperCase();
  if (role === 'ADMIN' || role === 'COMMUNITY') return true;
  const enabledFeatures = new Set((user.enabled_features || []).map(feature => String(feature).toLowerCase()));
  const featureFlags = user.feature_flags || {};
  return (
    PREMIUM_PLANS.has(String(user.plan || 'FREE').toUpperCase())
    || Boolean(user.jolly_early_access)
    || enabledFeatures.has('aina')
    || enabledFeatures.has('war_room')
    || enabledFeatures.has('jolly')
    || Boolean(featureFlags.aina)
    || Boolean(featureFlags.war_room)
    || Boolean(featureFlags.jolly)
  );
}

export default function AinaPage() {
  const { user } = useAuth();
  const { matterId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showEarlyBird, setShowEarlyBird] = useState(false);
  const [lockLandingViewport, setLockLandingViewport] = useState(true);
  const allowAccess = hasAinaAccess(user);
  const scrollContainerRef = useRef(null);
  const scrollToTop = () => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  const initialRunId = searchParams.get('run_id') || '';
  const initialThreadId = searchParams.get('thread_id') || '';
  const initialOpenChat = searchParams.get('open_chat') === '1';
  const initialPrompt = searchParams.get('prefill_prompt') || '';
  const journeyKey = searchParams.get('journey_key') || '';

  React.useEffect(() => {
    document.title = 'Aina — Advoq';
  }, []);

  React.useEffect(() => {
    if (!journeyKey) return;
    recordJourneyVisit(journeyKey, {
      surface: 'AINA',
      label: 'Aina',
      href: `${location.pathname}${location.search}`,
    });
  }, [journeyKey, location.pathname, location.search]);

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="aina-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
        <div
          ref={scrollContainerRef}
          className="p-4"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            height: '100%',
            overflowY: lockLandingViewport ? 'hidden' : 'auto',
          }}
        >
          {allowAccess ? (
            <div className="app-chat-header shrink-0" style={{ padding: '4px 4px 0' }}>
              <div className="app-chat-header__row">
                <div className="app-chat-toolbar">
                  <div className="app-chat-toolbar__group">
                    <span className="app-chat-button is-active">
                      <Eye size={13} /> Aina
                    </span>
                  </div>
                  <div className="app-chat-toolbar__group ml-auto">
                    <FeatureHistoryMenu feature="aina" matterId={matterId || ''} label="Recent Aina" dropdownAlign="right" />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {!allowAccess ? (
            <section
              className="advoq-card"
              style={{
                padding: '30px',
                borderRadius: '24px',
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start',
                background: 'linear-gradient(135deg, color-mix(in srgb, #ef4444 10%, var(--bg-surface)), color-mix(in srgb, #f97316 10%, var(--bg-surface)), color-mix(in srgb, var(--bg-elevated) 92%, transparent))',
                border: '1px solid var(--border-default)',
                boxShadow: 'var(--app-panel-shadow-soft)',
              }}
            >
              <Lock size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '4px' }} />
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)', marginBottom: '14px', border: '1px solid var(--border-subtle)' }}>
                  <Eye size={14} />
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                    Premium adversarial mirror
                  </span>
                </div>
                <h1 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '28px', fontFamily: 'var(--font-display)' }}>
                  Aina is a premium feature.
                </h1>
                <p style={{ margin: '12px 0 0', color: 'var(--text-secondary)', fontSize: '15px', lineHeight: 1.8, maxWidth: '860px' }}>
                  Once enabled, Aina plugs into Sabha, Jolly retention, matter retrieval, and research hand-offs so you can red-team a matter before filing, arguing, or advising.
                </p>
                <p style={{ margin: '16px 0 8px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Want free access forever?{' '}
                  <button
                    type="button"
                    onClick={() => setShowEarlyBird(true)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', fontWeight: 700, fontSize: '14px', padding: 0, textDecoration: 'underline' }}
                  >
                    Join the Early Believer Community.
                  </button>
                </p>
              </div>
            </section>
          ) : (
            <AinaWorkbench
              initialMatterId={matterId || ''}
              initialRunId={initialRunId}
              initialThreadId={initialThreadId}
              initialOpenChat={initialOpenChat}
              initialPrompt={initialPrompt}
              onScrollToTop={scrollToTop}
              onViewportLockChange={setLockLandingViewport}
            />
          )}
        </div>
      </main>
      {showEarlyBird && <EarlyBirdModal onClose={() => setShowEarlyBird(false)} onAccessGranted={() => window.location.reload()} />}
    </div>
  );
}
